import { useMarkNotificationAsRead, useNotifications } from "api/notifications";
import { routes } from "consts";
import { resolveLink } from "pages/Notifications/NotificationItem";
import { useEffect, useRef, useState } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { htmlParser } from "utils/htmlParser";
import { Transition, formatDate, getRelativeTime } from "utils";

interface DropdownNotificationsProps {
  align?: "left" | "right";
}

const NOTIFICATIONS_PER_PAGE = 6;

export function DropdownNotifications({ align }: DropdownNotificationsProps) {
  const { notifications = [], isLoading } = useNotifications(0, 60);
  const markAsReadMutation = useMarkNotificationAsRead();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      if (!dropdown.current) return;
      // @ts-ignore todo
      if (!dropdownOpen || dropdown.current.contains(e.target as Node) || trigger.current?.contains(e.target as Node))
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  const totalPages = Math.ceil(notifications.length / NOTIFICATIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * NOTIFICATIONS_PER_PAGE;
  const currentNotifications = notifications.slice(startIndex, startIndex + NOTIFICATIONS_PER_PAGE);
  const hasUnreadNotifications = notifications.some((n) => !n.isRead);

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600/80 ${
          dropdownOpen && "bg-gray-200 dark:bg-gray-800"
        }`}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Notifications</span>
        <svg className="fill-current text-gray-500/80 dark:text-gray-400/80" width={16} height={16} viewBox="0 0 16 16">
          <path d="M7 0a7 7 0 0 0-7 7c0 1.202.308 2.33.84 3.316l-.789 2.368a1 1 0 0 0 1.265 1.265l2.595-.865a1 1 0 0 0-.632-1.898l-.698.233.3-.9a1 1 0 0 0-.104-.85A4.97 4.97 0 0 1 2 7a5 5 0 0 1 5-5 4.99 4.99 0 0 1 4.093 2.135 1 1 0 1 0 1.638-1.148A6.99 6.99 0 0 0 7 0Z" />
          <path d="M11 6a5 5 0 0 0 0 10c.807 0 1.567-.194 2.24-.533l1.444.482a1 1 0 0 0 1.265-1.265l-.482-1.444A4.962 4.962 0 0 0 16 11a5 5 0 0 0-5-5Zm-3 5a3 3 0 0 1 6 0c0 .588-.171 1.134-.466 1.6a1 1 0 0 0-.115.82 1 1 0 0 0-.82.114A2.973 2.973 0 0 1 11 14a3 3 0 0 1-3-3Z" />
        </svg>
        {hasUnreadNotifications && (
          <div className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-gray-100 bg-red-500 dark:border-gray-900"></div>
        )}
      </button>

      {/* Phones: a sheet pinned under the header across the screen; from sm up: anchored under the bell */}
      <Transition
        data-testid="notifications-dropdown"
        className={`fixed inset-x-2 top-16 z-10 mt-1 max-h-[calc(100dvh-5rem)] origin-top-right overflow-y-auto rounded-lg border border-gray-200 bg-white py-1.5 shadow-lg sm:absolute sm:inset-x-auto sm:top-full sm:min-w-96 dark:border-gray-700/60 dark:bg-gray-800 ${
          align === "right" ? "sm:right-0" : "sm:left-0"
        }`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div ref={dropdown} onFocus={() => setDropdownOpen(true)} onBlur={() => setDropdownOpen(false)}>
          <div className="flex justify-between border-b-2 border-gray-200 px-4 pb-2 pt-1.5 text-xs font-semibold dark:border-gray-700">
            <span className="uppercase text-gray-400 dark:text-gray-500">Powiadomienia</span>
            <Link
              className="flex cursor-pointer items-center gap-2 hover:underline"
              to={routes.NOTIFICATIONS}
              onClick={() => setDropdownOpen(false)}
            >
              Zobacz wszystkie <FaArrowRightLong />
            </Link>
          </div>
          <ul>
            {currentNotifications.map((notification) => {
              let timerRef: NodeJS.Timeout;

              return (
                <li
                  key={notification.id}
                  className="border-b border-gray-200 last:border-0 dark:border-gray-700/60"
                  onMouseEnter={() => {
                    timerRef = setTimeout(async () => {
                      if (!notification.isRead) {
                        try {
                          await markAsReadMutation.mutateAsync(notification.id);
                        } catch (error) {
                          console.error("Failed to mark notification as read:", error);
                        }
                      }
                    }, 1500);
                  }}
                  onMouseLeave={() => clearTimeout(timerRef)}
                >
                  <Link
                    className="relative block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/20"
                    to={resolveLink(notification)}
                    onClick={() => {
                      // touch screens have no hover, so a tap marks the notification read itself
                      clearTimeout(timerRef);
                      if (!notification.isRead) markAsReadMutation.mutate(notification.id);
                      setDropdownOpen(false);
                    }}
                  >
                    <span className="mb-2 block text-sm">
                      {!notification.isRead && "📣 "}
                      {htmlParser.toHTML(notification.message)}
                    </span>
                    <span className="flex gap-2 text-xs font-medium text-gray-400 dark:text-gray-500">
                      <span className="text-primary-700 text-xs">{getRelativeTime(notification.notificationDate)}</span>
                      <span className="text-gray-400">{formatDate(notification.notificationDate, true, true)}</span>
                    </span>
                    {!notification.isRead && (
                      <div className="bg-primary-500 absolute right-1 top-[50%] h-2.5 w-2.5 rounded-full border-2 border-gray-100 opacity-60 dark:border-gray-900"></div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 px-2 py-2 text-xs text-gray-600 dark:text-gray-300">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`h-8 w-8 rounded sm:h-6 sm:w-6 ${currentPage === i + 1 ? "bg-primary-500 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
}
